import { Link } from 'react-router-dom';
import { Flame, TrendingUp, Gamepad2, Trophy, Zap, Star, ChevronRight } from 'lucide-react';
import type { CmsBlock } from './BlockRenderer';

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK = {
  user: { name: 'Marco', level: 12, nextLevel: 13 },
  points: 12450,
  nextLevelPoints: 13000,
  streak: 5,
  globalRank: 3,
  objectives: [
    {
      id: '1',
      title: 'Pronostici Perfetti',
      subtitle: 'Indovina 3 risultati oggi',
      progress: 1,
      total: 3,
      color: '#f0b100',
      icon: Zap,
    },
    {
      id: '2',
      title: 'Re dei Social',
      subtitle: 'Condividi un badge sbloccato',
      progress: 0,
      total: 1,
      color: '#ad46ff',
      icon: Star,
    },
  ],
  badges: [
    { id: '1', name: 'Primo Accesso', rarity: 'COMMON' },
    { id: '2', name: 'Settimana di Fuoco', rarity: 'RARE' },
  ],
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatMiniCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-[rgba(29,41,61,0.8)] border border-white/10 rounded-2xl flex flex-col items-center justify-center gap-1 px-3 py-3 min-w-[90px]">
      <p className="text-white font-bold text-sm leading-5 whitespace-nowrap">{value}</p>
      <p className="text-[#90a1b9] text-[10px] leading-[15px] text-center whitespace-nowrap">
        {label}
      </p>
    </div>
  );
}

function ObjectiveCard({
  title,
  subtitle,
  progress,
  total,
  color,
  icon: Icon,
}: (typeof MOCK.objectives)[number]) {
  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
  return (
    <div className="bg-[#0f172b] border border-white/5 rounded-2xl flex items-center gap-5 px-4 py-4">
      <div className="w-14 h-14 bg-[#1d293d] rounded-[14px] flex items-center justify-center shrink-0">
        <Icon size={28} className="text-[#90a1b9]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-end justify-between mb-1.5">
          <div>
            <p className="text-white font-bold text-base leading-6">{title}</p>
            <p className="text-[#90a1b9] text-xs leading-4">{subtitle}</p>
          </div>
          <span className="text-[#90a1b9] font-bold text-sm ml-2 shrink-0">
            {progress}/{total}
          </span>
        </div>
        {/* Progress bar */}
        <div className="relative h-2 rounded-full bg-[#020618] border border-white/5 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
}

function BadgeCard({ name, rarity }: { name: string; rarity: string }) {
  const rarityColors: Record<string, string> = {
    COMMON: '#00d492',
    UNCOMMON: '#3b82f6',
    RARE: '#a855f7',
    EPIC: '#f97316',
    LEGENDARY: '#f59e0b',
  };
  return (
    <div className="bg-[#0f172b] border border-white/5 rounded-2xl flex flex-col items-center py-4 px-3 gap-2">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00bc7d] to-[#00786f] flex items-center justify-center shadow-[0_10px_15px_0_rgba(0,188,125,0.2),0_4px_6px_0_rgba(0,188,125,0.2)]">
        <Trophy size={22} className="text-white" />
      </div>
      <p className="text-white font-bold text-xs text-center leading-4">{name}</p>
      <p
        className="font-bold text-[10px] uppercase tracking-widest"
        style={{ color: rarityColors[rarity] ?? '#00d492' }}
      >
        {rarity}
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LoyaltyDashboardBlock(_: { block?: CmsBlock }) {
  const progressPct = Math.min(100, Math.round((MOCK.points / MOCK.nextLevelPoints) * 100));

  return (
    <div className="p-10 flex flex-col gap-6">
      {/* ── Page heading ─────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-white font-extrabold text-[36px] leading-10 tracking-tight">
          Bentornato, {MOCK.user.name}!
        </h1>
        <p className="text-[#90a1b9] text-base mt-1">Hai 3 nuove sfide ad attenderti oggi.</p>
      </div>

      {/* ── Hero row: stats card + quick links ───────────────────────────── */}
      <div className="flex gap-6 h-[358px]">
        {/* Hero card */}
        <div className="flex-1 bg-[#0f172b] border border-white/5 rounded-3xl relative overflow-hidden">
          {/* Decorative glow blob */}
          <div className="absolute -top-24 right-0 w-64 h-64 bg-[rgba(0,188,125,0.2)] blur-[64px] rounded-full pointer-events-none" />
          {/* Bottom gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-44 bg-gradient-to-t from-[rgba(0,44,34,0.4)] to-transparent pointer-events-none" />

          <div className="relative z-10 p-8 flex flex-col h-full">
            {/* Saldo Punti */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#90a1b9] text-sm font-medium uppercase tracking-widest">
                  Saldo Punti
                </p>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-white font-black text-[60px] leading-none">
                    {MOCK.points.toLocaleString('it-IT')}
                  </span>
                  <span className="text-[#00bc7d] font-bold text-2xl tracking-widest">PTS</span>
                </div>
              </div>

              {/* Stat mini cards */}
              <div className="flex gap-4">
                <div className="bg-[rgba(29,41,61,0.8)] border border-white/10 rounded-2xl flex flex-col items-center justify-center gap-1.5 w-[100px] h-[124px]">
                  <Flame size={32} className="text-orange-400" />
                  <p className="text-white font-bold text-sm">{MOCK.streak} Giorni</p>
                  <p className="text-[#90a1b9] text-[10px] text-center leading-3">Streak Attuale</p>
                </div>
                <div className="bg-[rgba(29,41,61,0.8)] border border-white/10 rounded-2xl flex flex-col items-center justify-center gap-1.5 w-[100px] h-[124px]">
                  <TrendingUp size={32} className="text-[#00bc7d]" />
                  <p className="text-white font-bold text-sm">{MOCK.globalRank}° Posto</p>
                  <p className="text-[#90a1b9] text-[10px] text-center leading-3">
                    Classifica Globale
                  </p>
                </div>
              </div>
            </div>

            {/* Level progress */}
            <div className="mt-auto flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[#cad5e2] font-bold text-sm">
                  Livello {MOCK.user.level}
                </span>
                <span className="text-[#00d492] font-bold text-sm">
                  Mancano {(MOCK.nextLevelPoints - MOCK.points).toLocaleString('it-IT')} pts al
                  Livello {MOCK.user.nextLevel}
                </span>
              </div>
              <div className="relative h-4 rounded-full bg-[#020618] border border-white/5 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#00bc7d] via-[#00d492] to-[#9ae600]"
                  style={{ width: `${progressPct}%` }}
                >
                  <div className="absolute inset-0 bg-white/30 blur-[4px] rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick-link cards (right column) */}
        <div className="w-[237px] flex flex-col gap-[16px]">
          {/* Nuovi Giochi */}
          <Link
            to="/loyalty/games"
            className="flex-1 border border-[rgba(0,188,125,0.3)] rounded-3xl overflow-hidden relative block"
            style={{
              background: 'linear-gradient(144deg, rgb(0,153,102) 0%, rgb(0,79,59) 100%)',
            }}
          >
            <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3C%2Fsvg%3E')] pointer-events-none" />
            <div className="relative p-6 flex flex-col h-full">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center shadow-lg">
                  <Gamepad2 size={24} className="text-white" />
                </div>
                {/* Online dot */}
                <div className="w-3 h-3 bg-[#7ccf00] rounded-full shadow-[0_0_6px_rgba(124,207,0,0.8)]" />
              </div>
              <div className="mt-auto">
                <p className="text-white font-black text-xl leading-none">Nuovi Giochi</p>
                <p className="text-[#a4f4cf] text-sm mt-1">3 sfide disponibili oggi</p>
              </div>
            </div>
          </Link>

          {/* Contest */}
          <Link
            to="/loyalty/contests"
            className="flex-1 border border-[rgba(97,95,255,0.3)] rounded-3xl overflow-hidden relative block"
            style={{
              background: 'linear-gradient(144deg, rgb(79,57,246) 0%, rgb(15,23,43) 100%)',
            }}
          >
            <div className="relative p-6 flex flex-col h-full">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center shadow-lg">
                <Trophy size={24} className="text-white" />
              </div>
              <div className="mt-auto">
                <p className="text-white font-black text-xl leading-none">Contest</p>
                <p className="text-[#c6d2ff] text-sm mt-1">Partecipa e vinci premi reali</p>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* ── Bottom row: Objectives + Recent Badges ───────────────────────── */}
      <div className="grid grid-cols-2 gap-6">
        {/* I tuoi Obiettivi */}
        <div className="bg-[rgba(15,23,43,0.5)] border border-white/5 rounded-3xl p-8 flex flex-col gap-6">
          <h2 className="text-white font-bold text-xl leading-7">I tuoi Obiettivi</h2>
          <div className="flex flex-col gap-4">
            {MOCK.objectives.map((obj) => (
              <ObjectiveCard key={obj.id} {...obj} />
            ))}
          </div>
        </div>

        {/* Ultimi Badge Sbloccati */}
        <div className="bg-[rgba(15,23,43,0.5)] border border-white/5 rounded-3xl p-8 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-bold text-xl leading-7">Ultimi Badge Sbloccati</h2>
            <Link
              to="/loyalty/badges"
              className="flex items-center gap-1 text-[#00bc7d] font-bold text-sm hover:text-[#00d492] transition-colors"
            >
              Collezione Completa
              <ChevronRight size={16} />
            </Link>
          </div>
          <div className="flex gap-4">
            {MOCK.badges.map((badge) => (
              <BadgeCard key={badge.id} name={badge.name} rarity={badge.rarity} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
